<template>
  <div>
    <p class="subtitle">
      Your order summary is below.  Please review and click 'Change Plan' to finalize your order.
    </p>
    <table class="review-table">
      <tr>
        <th colspan="2">
          Current Plan
        </th>
      </tr>
      <tr>
        <td>{{ currentPlan.deal.public_name }} - Tier {{ currentPlan.tier }}</td>
        <td class="currency">
          <em>{{ currentPlan.price | currency }}</em>
        </td>
      </tr>
      <tr>
        <th colspan="2">
          New Selected Plan
        </th>
      </tr>
      <tr>
        <td>{{ newPlan.deal.public_name }} - Tier {{ newPlan.tier }}</td>
        <td class="currency">
          {{ newPlan.price | currency }}
        </td>
      </tr>
      <tr v-if="discountForRefund">
        <th>Subtotal</th>
        <th class="currency">
          -{{ maxRefundableAmount | currency }}
        </th>
      </tr>
      <tr v-if="discountForRefund">
        <td>{{ discountType }} Discount</td>
        <td class="currency">
          -{{ discountForRefund | currency }}
        </td>
      </tr>
      <tr v-if="memberDiscount && !isDowngrade">
        <td> {{ discountType }} Discount</td>
        <td class="currency">
          -{{ memberDiscount | currency }}
        </td>
      </tr>
      <tr>
        <th>Total</th>
        <th class="currency">
          {{ (newPlan.price - currentPlan.price) - memberDiscount | currency }}
        </th>
      </tr>
    </table>
    <div v-if="isDowngrade">
      <p class="result-total">
        Total amount to refund:
        <strong>
          {{ ((newPlan.price - currentPlan.price) - memberDiscount) * -1 | currency }}</strong>
      </p>
      <ul>
        <li
          v-for="refund_item in refundItems"
          :key="refund_item.payment_type"
        >
          <span v-if="!refund_item.discountType">
            <strong>
              {{ (refund_item.amount + memberDiscount) | currency }}
            </strong> to {{ refund_item.paymentType }}
          </span>
          <span v-if="refund_item.discountType">
            <strong>{{ refund_item.amount | currency }}</strong>
            to {{ refund_item.discountType }} {{ refund_item.paymentType }}
          </span>
        </li>
      </ul>
    </div>
    <div v-else>
      <p class="result-total">
        <strong>{{ (newPlan.price - currentPlan.price) - memberDiscount | currency }}</strong>
        will be charged to the payment method selected below:
      </p>
      <div
        v-if="paymentMethod === 'creditcard' && selectedCard"
        class="card-image"
      >
        <img
          v-if="selectedCard.brand === 'Visa'"
          :src="VisaLogo"
        >
        <img
          v-if="selectedCard.brand === 'Mastercard'"
          :src="MastercardLogo"
        >
        <span>Ending in: <strong>{{ selectedCard.last4 }}</strong></span>
      </div>
      <div
        v-if="paymentMethod === 'paypal'"
        class="card-image"
      >
        <img :src="PayPalLogo">
        <span>PayPal</span>
      </div>
      <div
        v-if="paymentMethod === 'credit'"
        class="card-image"
      >
        <img
          :src="TacoLogo"
          class="taco-fill"
        >
        <span>Credits</span>
      </div>
    </div>
  </div>
</template>

<script>
import isNumber from 'lodash/isNumber';

import PayPalLogo from '../../../public/paypal.svg';
import VisaLogo from '../../../public/visa.svg';
import MastercardLogo from '../../../public/mastercard.svg';
import TacoLogo from '../../../public/as-taco.svg';

export default {
  name: 'ReviewPlan',
  filters: {
    // TODO: make global filter
    currency(value) {
      const money = parseFloat(value);
      const cents = (money % 1.0);
      if (cents > 0) {
        return `$${money.toFixed(2)}`;
      }

      return `$${money}`;
    },
  },
  props: {
    isDowngrade: {
      type: Boolean,
      required: true,
    },
    currentPlan: {
      type: Object,
      required: true,
    },
    newPlan: {
      type: Object,
      required: true,
    },
    selectedCard: {
      type: Object,
      default: () => ({}),
    },
    paymentMethod: {
      type: String,
      default: '',
    },
    memberDiscount: {
      type: Number,
      required: true,
    },
    discountType: {
      type: String,
      default: '',
    },
  },
  data() {
    return ({
      VisaLogo,
      MastercardLogo,
      PayPalLogo,
      TacoLogo,
    });
  },
  computed: {
    discountForRefund() {
      return this.currentPlan.invoice.enhanced_invoice_items
        .reduce((accumulator, currentValue) => {
          if (currentValue.deal_plan.tier > this.newPlan.tier) {
            return accumulator + Number(currentValue.discount);
          }
          return accumulator;
        }, 0);
    },
    maxRefundableAmount() {
      const maxRefundableAmount = this.currentPlan.price - this.newPlan.price;

      return maxRefundableAmount - this.discountForRefund;
    },
    refundItems() {
      // Cash + credits - discounts
      const { maxRefundableAmount } = this;
      const enhancedInvoices = this.currentPlan.invoice.enhanced_invoice_items;
      const newPlanTier = this.newPlan.tier;
      let items = [];
      let refundableAmount = 0;
      let discounts = 0;

      enhancedInvoices.forEach((element) => {
        if (element.deal_plan.tier > newPlanTier) {
          const cashToRefund = element.cash - element.cash_refunded;
          const creditToRefund = element.credit - element.credit_refunded;
          const invoiceItem = items.find((x) => x.paymentType === element.payment_type);
          let amountToRefund = cashToRefund && element.payment_type !== 'Credit' ? cashToRefund : creditToRefund;
          if (refundableAmount + amountToRefund > maxRefundableAmount) {
            amountToRefund = maxRefundableAmount - refundableAmount;
          }

          if (refundableAmount > maxRefundableAmount) return;

          if (element.discount) {
            discounts += Number(element.discount);
          }

          if (invoiceItem) {
            invoiceItem.amount += amountToRefund;
          } else if (amountToRefund) {
            items.push({ amount: amountToRefund, paymentType: element.payment_type });
          }

          refundableAmount += amountToRefund;
        }
      });

      if (refundableAmount < maxRefundableAmount) {
        const invoiceItem = items.find(
          (x) => x.paymentType === this.currentPlan.original_invoice_payment_type,
        );
        const amountToRefund = maxRefundableAmount - refundableAmount;

        if (invoiceItem) {
          invoiceItem.amount += amountToRefund;
        } else if (amountToRefund) {
          items.push({
            amount: amountToRefund,
            paymentType: this.currentPlan.original_invoice_payment_type,
          });
        }
      }

      if (discounts > 0);
      items.push({ amount: discounts, paymentType: 'discount', discountType: this.discountType });

      // eslint-disable-next-line no-restricted-globals
      items = items.filter((item) => isNumber(item.amount) && parseFloat(item.amount) > 0);
      return items;
    },
  },
};

</script>

<style lang="scss" scoped>
@import "../variables.scss";

.subtitle {
  font-size: 14px;
  font-weight: 500;
  margin: 20px 0;
}

.review-table {
  border: 1px solid #e5e5e9;
  width: 100%;
  border-collapse: collapse;

  th {
    font-size: 12px;
    line-height: 12px;
    color: #272727;
    padding: 15px;
    font-weight: 600;
  }

  td {
    border-top: 1px solid #e5e5e9;
    border-bottom: 1px solid #e5e5e9;
    font-size: 12px;
    font-weight: 500;
    line-height: 12px;
    color: #4F5A68;
    padding: 15px;
  }
}

.currency {
  text-align: right;
}

.result-total {
  margin-top: 20px;
}

.card-image {
  display: flex;
  align-items: center;
  font-size: 12px;

  img {
    vertical-align: middle;
    margin-right: 5px;
    height: 20px;
  }
}

.taco-fill {
  fill: $warning-color;
}

</style>
